import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchJobs } from '../../Redux/ActionCreators/jobsAction'
import './createJob.scss'
import './loading.scss'
import AddJobDetails from './AddJobDetails'
import SelectAIFeatures from './SelectAIFeatures'
import InviteStep from './InviteStep'
import { useNavigate } from 'react-router-dom'
import { canCreateJob, canEditJob } from '../../constants/rbac'

const STAGES = [
    { step: 1, label: 'Add job details' },
    { step: 2, label: 'Select screening' },
    { step: 3, label: 'Invite Candidates' },
]

const ACTIVE_STATUS_ID = "14107328-840b-4ad0-904f-8a2243720f70";

function CreateJob() {
    const [currentStage, setCurrentStage] = useState(1);
    const [jobId, setJobId] = useState(null);
    const [restrictedEditMode, setRestrictedEditMode] = useState(false);

    // Stable UUID for the entire wizard session — generated once on mount
    const flowSessionId = useRef(
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `flow-${Date.now()}-${Math.random().toString(36).slice(2)}`
    ).current;
    const [showBanner, setShowBanner] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const [transitionMessage, setTransitionMessage] = useState("");
    const hasAnyJobs = useSelector((state) => state.jobs?.hasAnyJobs);
    const jobList = useSelector((state) => state.jobs?.jobList || []);
    const loading = useSelector((state) => state.jobs?.loading);
    const meDetails = useSelector((state) => state.auth?.meDetails);
    const allowCreateJob = canCreateJob(meDetails);
    const allowEditJob = canEditJob(meDetails);
    const prevLoadingRef = useRef(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchJobs(1, 100, ACTIVE_STATUS_ID, "", "", "", "", ""));
    }, [dispatch]);

    useEffect(() => {
        // Block direct URL access when role lacks create/edit permission.
        if (!meDetails) return;
        if (!restrictedEditMode && !allowCreateJob && !jobId) {
            navigate('/jobs', { replace: true });
            return;
        }
        if (jobId && !allowEditJob) {
            navigate('/jobs', { replace: true });
        }
    }, [meDetails, allowCreateJob, allowEditJob, restrictedEditMode, jobId, navigate]);

    useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = loading;
        if (loading || !wasLoading) return;
        if (jobId) return;
        // Recruiters are never locked into job creation, so treat them as having jobs
        const isRecruiter = (localStorage.getItem("roleName") || "").toUpperCase() === "RECRUITER";
        if (jobList.length > 0 || isRecruiter) {
            dispatch({ type: "SET_HAS_ANY_JOBS", payload: true });
        } else {
            dispatch({ type: "SET_HAS_ANY_JOBS", payload: false });
        }
    }, [loading, jobList.length, jobId, dispatch]);

    const showNoJobsPopup = () => {
        window.dispatchEvent(new CustomEvent("SHOW_NO_JOBS_POPUP"));
    };

    const goNext = (id, skipIncrement = false) => {
        if (id && typeof id === 'string') {
            setJobId(id);
        }
        setTransitioning(false);
        if (!skipIncrement) {
            setCurrentStage((s) => Math.min(s + 1, STAGES.length))
        }
    };

    const handleTransitionStart = (message) => {
        setTransitionMessage(message);
        setTransitioning(true);
        setCurrentStage((s) => Math.min(s + 1, STAGES.length));
    };

    const handleTransitionError = () => {
        setTransitioning(false);
        setCurrentStage((s) => Math.max(s - 1, 1));
    };

    const goBack = () => {
        if (currentStage === 1) {
            if (hasAnyJobs === false) { showNoJobsPopup(); return; }
            navigate("/jobs")
        } else {
            setCurrentStage((s) => Math.max(s - 1, 1))
        }
    }

    return (
        <>
            <div className="cj-wrapper">
                {/* Top header */}
                <div className="cj-header">
                    <button className="cj-header-back" onClick={() => { if (hasAnyJobs === false) { showNoJobsPopup(); return; } navigate("/jobs"); }} aria-label="Back">
                        <img src="/images/leftarrow-black-icon.svg" alt="Back" width={32} height={32} />
                    </button>
                    <div className="cj-header-info">
                        <h2 className="cj-header-title">{restrictedEditMode ? 'Edit Job' : 'Create Job'}</h2>
                        <span className="cj-header-step">
                            {currentStage}/{STAGES.length}: {STAGES[currentStage - 1].label}
                        </span>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="cj-progress-bar">
                    {STAGES.map((stage) => (
                        <div
                            key={stage.step}
                            className={`cj-progress-segment ${currentStage >= stage.step ? 'cj-progress-segment--filled' : ''}`}
                        />
                    ))}
                </div>

                {/* Mobile info banner */}
                {showBanner && (
                    <div className='cj-wrapper-sm'>
                        <img src='/images/lightbulb-on.svg' alt='' />
                        <h3>For best experience please use Laptop or desktop to create job</h3>
                        <button className='cj-wrapper-sm-close' onClick={() => setShowBanner(false)} aria-label="Close">&#x2715;</button>
                    </div>
                )}

                {/* Stage content */}
                <div className="cj-content">
                    {currentStage === 1 && <AddJobDetails initialJobId={jobId} onNext={goNext} onTransitionStart={handleTransitionStart} onTransitionError={handleTransitionError} flowSessionId={flowSessionId} onRestrictedEditChange={setRestrictedEditMode} />}
                    {currentStage === 2 && <SelectAIFeatures jobId={jobId} onNext={goNext} onBack={goBack} transitioning={transitioning} transitionMessage={transitionMessage} flowSessionId={flowSessionId} isRestrictedEdit={restrictedEditMode} />}
                    {currentStage === 3 && <InviteStep jobId={jobId} flowSessionId={flowSessionId} />}
                </div>
            </div>
        </>
    )
}

export default CreateJob
